import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PackageCheck } from 'lucide-react';
import { useOrders } from '../hooks/use-orders';
import { OrderCard } from '@/components/organisms/cards';
import { SearchBar } from '@/components/molecules/search-bar';
import { EmptyState } from '@/components/organisms/empty-state';
import { Skeleton } from '@/components/atoms/skeleton';
import { Card } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { StatusBadge } from '@/components/molecules/status-badge';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime, formatPrice, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { Order } from '@/lib/types';

// Server status values for filtering
const ORDER_STATUSES = [
  'all',
  'waiting_validation',
  'validated',
  'in_progress',
  'in_delivery',
  'delivered',
  'canceled',
] as const;


// Extract customer name from string or object
function getCustomerName(customer: Order['customer']): string {
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  if (typeof customer === 'object') {
    if (customer.fullName) return customer.fullName;
    const parts = [customer.firstName, customer.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  }
  return '-';
}

export function OrderListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<string>('all');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { data, isLoading, error } = useOrders({
    search,
    status: status === 'all' ? undefined : status,
    page,
    limit: pageSize,
  });

  const orders = data?.orders ?? [];
  const totalPages = data?.totalPages ?? 1;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setPage(1);
  }, [search, status]);

  const handleRowClick = (order: Order) => {
    navigate(`/orders/${order.orderId}`);
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Order>[] = React.useMemo(
    () => [
      {
        id: 'orderId',
        header: t('orders.order'),
        cell: (order) => (
          <span className="font-mono text-sm font-medium">#{order.orderId}</span>
        ),
        sortable: true,
      },
      {
        id: 'customer',
        header: t('orders.customer'),
        cell: (order) => (
          <span className="font-medium">{getCustomerName(order.customer)}</span>
        ),
      },
      {
        id: 'items',
        header: t('orders.items'),
        cell: (order) => (
          <span className="text-muted-foreground">
            {order.products?.length ?? 0} article{(order.products?.length ?? 0) > 1 ? 's' : ''}
          </span>
        ),
      },
      {
        id: 'total',
        header: t('orders.total'),
        cell: (order) => (
          <span className="font-bold">
            {formatPrice(getAmountFromPriceObject(order.total_price, currency), currency)}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'earnings',
        header: t('orders.yourEarnings', 'Vos gains'),
        cell: (order) => {
          const netAmount = order.earnings?.netAmount ? getAmountFromPriceObject(order.earnings.netAmount, currency) : null;
          return (
            <span className="font-semibold text-green-600 dark:text-green-400">
              {netAmount !== null ? formatPrice(netAmount, currency) : '-'}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (order) => <StatusBadge status={order.status} />,
      },
      {
        id: 'date',
        header: t('orders.orderDate'),
        cell: (order) => (
          <span className="text-sm text-muted-foreground">
            {formatRelativeTime(order.orderDate, 'fr')}
          </span>
        ),
        sortable: true,
      },
    ],
    [t, currency]
  );

  // Card renderer for mobile view
  const renderCard = (order: Order, index: number) => (
    <OrderCard key={order.id || `order-${index}`} order={order} />
  );

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex justify-between">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('orders.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SearchBar
            onSearch={setSearch}
            placeholder={t('orders.searchPlaceholder')}
            className="sm:max-w-xs"
          />
          <Button variant="outline" asChild>
            <Link to="/orders/pickups">
              <PackageCheck />
              {t('orders.pickupConfirmation.navLabel')}
            </Link>
          </Button>
        </div>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="flex-wrap">
            {ORDER_STATUSES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {t(`orders.status.${s}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <ResponsiveDataList
        data={orders}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(order, index) => order.id || `order-${index}`}
        cardGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyState={
          <EmptyState
            title={t('orders.emptyTitle')}
            description={t('orders.emptyDescription')}
          />
        }
      />
    </div>
  );
}
